import type { WorkflowRunSourcePayload } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import TranscriptionIssueView from '@UI/Store/WorkflowRuns/Views/TranscriptionIssueView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * One input of a run. `declaredDocumentTypeId` is set on a document carried automatically or chosen for
 * a type. Titles are never carried: resolve them by `interviewId` / `documentId` / `importedDocumentId` from the other stores.
 * The server keeps the source row when its interview or document is deleted and nulls its ids, which
 * is how a deleted source is recognised.
 */
class WorkflowRunSourceView {
	readonly id: number;
	readonly category: LabeledValueView;
	readonly interviewId: number | null;
	readonly documentId: number | null;
	readonly importedDocumentId: number | null;
	readonly declaredDocumentTypeId: number | null;
	readonly addedAt: Date;
	readonly state: LabeledValueView;
	/** Why the source is in this state, in French, when the server says so. */
	readonly stateReason: string | null;
	readonly canTranscribe: boolean;
	readonly transcriptionIssue: TranscriptionIssueView | null;

	constructor(payload: WorkflowRunSourcePayload) {
		this.id = payload.id;
		this.category = new LabeledValueView(payload.category);
		this.interviewId = payload.interview?.id ?? null;
		this.documentId = payload.document?.id ?? null;
		this.importedDocumentId = payload.importedDocument?.id ?? null;
		this.declaredDocumentTypeId = payload.declaredDocumentType?.id ?? null;
		this.addedAt = new Date(payload.addedAt);
		this.state = new LabeledValueView(payload.state);
		this.stateReason = payload.stateReason;
		this.canTranscribe = payload.canTranscribe;
		this.transcriptionIssue =
			payload.transcriptionIssue !== null
				? new TranscriptionIssueView(payload.transcriptionIssue)
				: null;

		Object.freeze(this);
	}

	get isDeleted(): boolean {
		return this.interviewId === null && this.documentId === null && this.importedDocumentId === null;
	}

	/** The recording is still on its way, its transcription still runs, or the import is being converted. */
	get isInProgress(): boolean {
		return (
			this.state.value === 'uploading' ||
			this.state.value === 'transcribing' ||
			this.state.value === 'converting'
		);
	}
}

export default WorkflowRunSourceView;
